import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider, ToggleButtonGroup, ToggleButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed', 'Not Relevant', 'Waiting for Approval', 'Waiting on Someone Else'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

// 11.3: exact priority colours requested
const PRIORITY_COLOR_HEX = {
    Critical: '#d32f2f', // red
    High: '#ed6c02',     // orange
    Medium: '#1976d2',   // blue
    Low: '#000000',      // black
};
const COMPLETED_COLOR_HEX = '#2e7d32'; // green

const COMPLETED_RETENTION_DAYS = 7;

const emptyTask = {
    subject: '',
    dueDate: '',
    reminderDate: '',
    status: 'Not Started',
    priority: 'Medium',
    description: '',
    assignedTo: '',
    // 11.2: audit fields
    createdBy: '',
    createdByName: '',
    createdAt: '',
    assignedAt: '',
    receivedAt: '',
    completedAt: '',
    history: [], // [{ action, fromUserId, fromUserName, toUserId, toUserName, byUserId, byUserName, date }]
};

const getUserDisplayName = (u) => (u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.emailAddress || 'Unknown') : 'Unknown');
const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '-' : d.toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const daysSince = (iso) => {
    if (!iso) return Infinity;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return Infinity;
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
};

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};

function TaskDialog({ open, onClose, onSave, onMarkReceived, task, administrators, isDisabled, currentUser }) {
    const [form, setForm] = useState(emptyTask);

    useEffect(() => {
        if (!open) return;
        if (task) {
            // Legacy tasks may only have `isDone`/`description` (no status/subject yet) - infer sensible defaults
            const inferredStatus = task.status || (task.isDone ? 'Completed' : 'Not Started');
            setForm({ ...emptyTask, ...task, status: inferredStatus });
        } else {
            setForm({ ...emptyTask });
        }
    }, [open, task]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        if (!form.subject) return;
        onSave(form);
    };

    // 11.2: creator / assignee audit info visible to both parties
    const isAssignee = task?.assignedTo && currentUser?.id && String(task.assignedTo) === String(currentUser.id);
    const canMarkReceived = task?.id && isAssignee && !task.receivedAt;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{task?.id ? 'Edit Task' : 'New Task'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Subject" name="subject" value={form.subject} onChange={handleChange} disabled={isDisabled} autoFocus />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth type="date" label="Due Date" name="dueDate" InputLabelProps={{ shrink: true }} value={form.dueDate || ''} onChange={handleChange} disabled={isDisabled} inputProps={{ max: '9999-12-31' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth type="date" label="Reminder Date" name="reminderDate" InputLabelProps={{ shrink: true }} value={form.reminderDate || ''} onChange={handleChange} disabled={isDisabled} inputProps={{ max: '9999-12-31' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField select fullWidth label="Assign To" name="assignedTo" value={form.assignedTo || ''} onChange={handleChange} disabled={isDisabled}>
                            <MenuItem value=""><em>Matter Owner</em></MenuItem>
                            {administrators.map(admin => (
                                <MenuItem key={admin.id} value={admin.id}>{admin.firstName} {admin.lastName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Status" name="status" value={form.status || 'Not Started'} onChange={handleChange} disabled={isDisabled}>
                            {STATUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Priority" name="priority" value={form.priority || 'Medium'} onChange={handleChange} disabled={isDisabled}>
                            {PRIORITY_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Description</Typography>
                        <Box sx={{ '& .ql-editor': { minHeight: '260px' }, bgcolor: 'background.paper' }}>
                            <ReactQuill
                                theme="snow"
                                value={form.description || ''}
                                onChange={(value) => setForm(prev => ({ ...prev, description: value }))}
                                readOnly={isDisabled}
                                modules={QUILL_MODULES}
                            />
                        </Box>
                    </Grid>

                    {/* 11.2: Task Audit / Information */}
                    {task?.id && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <HistoryIcon fontSize="small" color="action" />
                                <Typography variant="subtitle2" color="text.secondary">Task Audit Trail</Typography>
                            </Stack>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Created By</Typography>
                                        <Typography variant="body2">{form.createdByName || 'Unknown'} — {formatDateTime(form.createdAt)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Currently Assigned</Typography>
                                        <Typography variant="body2">
                                            {form.assignedTo ? (administrators.find(a => String(a.id) === String(form.assignedTo)) ? getUserDisplayName(administrators.find(a => String(a.id) === String(form.assignedTo))) : 'Assigned') : 'Matter Owner'}
                                            {form.assignedAt ? ` — since ${formatDateTime(form.assignedAt)}` : ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Date/Time Received</Typography>
                                        <Typography variant="body2">{formatDateTime(form.receivedAt)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Date/Time Completed</Typography>
                                        <Typography variant="body2">{formatDateTime(form.completedAt)}</Typography>
                                    </Grid>
                                    {(form.history || []).length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Reassignment History</Typography>
                                            <Stack spacing={0.5}>
                                                {form.history.map((h, idx) => (
                                                    <Typography key={idx} variant="caption" display="block">
                                                        {formatDateTime(h.date)}: {h.byUserName || 'Someone'} moved this task from <strong>{h.fromUserName || 'Matter Owner'}</strong> to <strong>{h.toUserName || 'Matter Owner'}</strong>
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                                {canMarkReceived && (
                                    <Box sx={{ mt: 2 }}>
                                        <Button size="small" variant="outlined" onClick={() => onMarkReceived(task)}>
                                            Mark as Received
                                        </Button>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" onClick={handleSave} disabled={isDisabled || !form.subject}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function TasksTab({ formData, handleFieldChange, isEditingMode, user, matterId }) {
    const tasks = formData?.tasksList || [];
    const [administrators, setAdministrators] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTask, setActiveTask] = useState(null);
    const [viewMode, setViewMode] = useState('current'); // 'current' | 'archive' - 11.3
    const [archiveSearch, setArchiveSearch] = useState('');

    useEffect(() => {
        axios.get('/api/users/administrators', { withCredentials: true })
            .then(res => setAdministrators(res.data || []))
            .catch(err => console.error("Failed to fetch admins", err));
    }, []);

    const saveToDB = async (list) => {
        if (!matterId) return;
        try {
            await axios.put(`/api/legal-matter/${matterId}`, { tasksList: list });
        } catch (e) {
            console.error("Auto-save failed", e);
        }
    };

    const handleOpenNew = () => {
        setActiveTask(null);
        setDialogOpen(true);
    };

    const handleOpenExisting = (task) => {
        setActiveTask(task);
        setDialogOpen(true);
    };

    const handleSaveTask = (form) => {
        const nowIso = new Date().toISOString();
        const currentUserName = getUserDisplayName(user);
        let updatedList;

        if (activeTask?.id) {
            const oldTask = tasks.find(t => t.id === activeTask.id) || {};
            let newForm = { ...form };

            // 11.1 / 11.2g: assignment changed - log history + notify (notification is
            // handled server-side by diffing the saved tasksList, see PUT /api/legal-matter/:id)
            if (String(form.assignedTo || '') !== String(oldTask.assignedTo || '')) {
                const fromAdmin = administrators.find(a => String(a.id) === String(oldTask.assignedTo));
                const toAdmin = administrators.find(a => String(a.id) === String(form.assignedTo));
                const historyEntry = {
                    action: 'reassigned',
                    fromUserId: oldTask.assignedTo || null,
                    fromUserName: oldTask.assignedTo ? getUserDisplayName(fromAdmin) : 'Matter Owner',
                    toUserId: form.assignedTo || null,
                    toUserName: form.assignedTo ? getUserDisplayName(toAdmin) : 'Matter Owner',
                    byUserId: user?.id || null,
                    byUserName: currentUserName,
                    date: nowIso,
                };
                newForm.assignedAt = nowIso;
                newForm.receivedAt = ''; // reset - the new assignee hasn't received it yet
                newForm.history = [...(oldTask.history || []), historyEntry];
            }

            // 11.2f: stamp/clear completedAt when status crosses in/out of "Completed"
            if (form.status === 'Completed' && oldTask.status !== 'Completed') {
                newForm.completedAt = nowIso;
            } else if (form.status !== 'Completed') {
                newForm.completedAt = '';
            }

            updatedList = tasks.map(item => item.id === activeTask.id ? { ...newForm, id: activeTask.id } : item);
        } else {
            // New task
            const toAdmin = administrators.find(a => String(a.id) === String(form.assignedTo));
            const newTask = {
                ...form,
                id: -Math.floor(Math.random() * 1000000),
                createdBy: user?.id || '',
                createdByName: currentUserName,
                createdAt: nowIso,
                assignedAt: form.assignedTo ? nowIso : '',
                receivedAt: '',
                completedAt: form.status === 'Completed' ? nowIso : '',
                history: form.assignedTo ? [{
                    action: 'assigned',
                    fromUserId: null,
                    fromUserName: null,
                    toUserId: form.assignedTo,
                    toUserName: getUserDisplayName(toAdmin),
                    byUserId: user?.id || null,
                    byUserName: currentUserName,
                    date: nowIso,
                }] : [],
            };
            updatedList = [...tasks, newTask];
        }

        handleFieldChange({ target: { name: 'tasksList', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
        setDialogOpen(false);
    };

    const handleMarkReceived = (task) => {
        const updatedList = tasks.map(item => item.id === task.id ? { ...item, receivedAt: new Date().toISOString() } : item);
        handleFieldChange({ target: { name: 'tasksList', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
        setActiveTask(prev => prev && prev.id === task.id ? { ...prev, receivedAt: new Date().toISOString() } : prev);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            const updatedList = tasks.filter(item => item.id !== id);
            handleFieldChange({ target: { name: 'tasksList', value: updatedList, type: 'array' } });
            saveToDB(updatedList);
        }
    };

    const isDisabled = !isEditingMode || user?.role === 'guest';

    // 11.3: current list = not completed, OR completed within the retention window.
    // Old/Completed view = every completed task ever, searchable - nothing is ever deleted.
    const isTaskCompleted = (item) => item.status ? item.status === 'Completed' : !!item.isDone;
    const visibleTasks = viewMode === 'archive'
        ? tasks
            .filter(isTaskCompleted)
            .filter(item => {
                if (!archiveSearch.trim()) return true;
                const term = archiveSearch.toLowerCase();
                return (item.subject || '').toLowerCase().includes(term) || (item.description || '').replace(/<[^>]*>/g, '').toLowerCase().includes(term);
            })
        : tasks.filter(item => !isTaskCompleted(item) || daysSince(item.completedAt) <= COMPLETED_RETENTION_DAYS);

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Matter Tasks
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(e, val) => val && setViewMode(val)}>
                        <ToggleButton value="current">Current Tasks</ToggleButton>
                        <ToggleButton value="archive">Old / Completed Tasks</ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenNew} disabled={isDisabled}>
                        New Task
                    </Button>
                </Stack>
            </Stack>

            {viewMode === 'archive' && (
                <TextField
                    fullWidth
                    size="small"
                    label="Search Old / Completed Tasks"
                    placeholder="Search by subject or description..."
                    value={archiveSearch}
                    onChange={(e) => setArchiveSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />
            )}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Reminder Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleTasks.map((item) => {
                            const isCompleted = isTaskCompleted(item);
                            const isOverdue = !isCompleted && item.dueDate && new Date(item.dueDate) < new Date();
                            const assignedAdmin = administrators.find(a => String(a.id) === String(item.assignedTo));
                            // 11.3: priority colour, overridden to green once completed
                            const rowColor = isCompleted ? COMPLETED_COLOR_HEX : (PRIORITY_COLOR_HEX[item.priority] || PRIORITY_COLOR_HEX.Medium);
                            const wasReassigned = (item.history || []).length > 0;

                            return (
                                <TableRow
                                    key={item.id}
                                    hover
                                    onClick={() => handleOpenExisting(item)}
                                    sx={{ cursor: 'pointer', backgroundColor: isCompleted ? '#f0fdf4' : 'inherit' }}
                                >
                                    <TableCell sx={{ borderLeft: `4px solid ${rowColor}` }}>
                                        <Typography variant="body2" sx={{ textDecoration: isCompleted ? 'line-through' : 'none', fontWeight: 500, color: rowColor }}>
                                            {item.subject || item.description?.replace(/<[^>]*>/g, '') || '(No subject)'}
                                        </Typography>
                                        {item.createdByName && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Created by {item.createdByName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.assignedTo ? (assignedAdmin ? getUserDisplayName(assignedAdmin) : 'Assigned') : 'Matter Owner'}
                                        </Typography>
                                        {/* 11.1: clear indicator that this task has been assigned/passed to someone */}
                                        {item.assignedTo && (
                                            <Chip
                                                size="small"
                                                label={wasReassigned ? 'Reassigned' : 'Assigned'}
                                                color={item.receivedAt ? 'success' : 'warning'}
                                                variant="outlined"
                                                sx={{ mt: 0.5 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: isOverdue ? 'error.main' : 'inherit', fontWeight: isOverdue ? 'bold' : 'normal' }}>
                                            {item.dueDate || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{item.reminderDate || '-'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={item.status || 'Not Started'} variant="outlined" sx={{ color: rowColor, borderColor: rowColor }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={item.priority || 'Medium'} variant="outlined" sx={{ color: PRIORITY_COLOR_HEX[item.priority] || PRIORITY_COLOR_HEX.Medium, borderColor: PRIORITY_COLOR_HEX[item.priority] || PRIORITY_COLOR_HEX.Medium }} />
                                    </TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)} disabled={isDisabled}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {visibleTasks.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                    {viewMode === 'archive' ? 'No old/completed tasks found.' : 'No tasks created. Click "New Task" to add one.'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TaskDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSaveTask}
                onMarkReceived={handleMarkReceived}
                task={activeTask}
                administrators={administrators}
                isDisabled={isDisabled}
                currentUser={user}
            />
        </Box>
    );
}
